<template>
  <div
    v-show="distance > 0 || refreshing"
    class="pull-to-refresh"
    :class="{ 'pull-to-refresh--settling': !dragging }"
    :style="{ transform: `translate(-50%, ${offset}px)` }"
    data-testid="pull-to-refresh"
    role="status"
    :aria-label="label"
  >
    <v-progress-circular
      :indeterminate="refreshing"
      :value="refreshing ? 0 : progress"
      :rotate="refreshing ? 0 : progress * 2.7"
      size="24"
      width="3"
      color="primary"
    />
  </div>
</template>

<script>
// Один жест «потяни, чтобы обновить» на всё приложение (только нативные
// сборки). Какие страницы его включают и что значит «обновить» — в
// src/utils/pullToRefresh.js.
import {
  canStartPull,
  pullDistance,
  markUserInput,
  markClean,
  PULL_THRESHOLD,
} from "@/utils/pullToRefresh";

// Индикатор не прячется мгновенно: иначе на быстрой загрузке он мелькает.
const MIN_SPIN_MS = 500;
// Страховка на случай, если promise загрузки так и не завершится.
const MAX_SPIN_MS = 10000;

export default {
  name: "PullToRefresh",
  props: {
    // () => Promise — что делать по жесту.
    refresh: { type: Function, required: true },
  },
  data() {
    return {
      startX: 0,
      startY: 0,
      // null — касание ещё не решено, true — тянем, false — не наш жест.
      tracking: null,
      dragging: false,
      distance: 0,
      refreshing: false,
    };
  },
  computed: {
    progress() {
      return Math.min(100, Math.round((this.distance / PULL_THRESHOLD) * 100));
    },
    label() {
      if (this.refreshing) return this.$t("custom.refreshing");
      if (this.distance >= PULL_THRESHOLD) return this.$t("custom.release_to_refresh");
      return this.$t("custom.pull_to_refresh");
    },
    // Кружок выезжает из-под шапки и останавливается чуть ниже неё.
    offset() {
      return this.refreshing ? PULL_THRESHOLD * 0.75 : this.distance;
    },
  },
  watch: {
    // Новый экран — ввода на нём ещё не было.
    "$route.fullPath"() {
      markClean();
    },
  },
  mounted() {
    // Capture: ввод внутри компонентов, гасящих всплытие, тоже считается.
    window.addEventListener("input", markUserInput, true);
    window.addEventListener("touchstart", this.onTouchStart, { passive: true });
    // Не passive: пока тянем, гасим родной bounce / overscroll WebView.
    window.addEventListener("touchmove", this.onTouchMove, { passive: false });
    window.addEventListener("touchend", this.onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", this.onTouchCancel, { passive: true });
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  },
  beforeDestroy() {
    window.removeEventListener("input", markUserInput, true);
    window.removeEventListener("touchstart", this.onTouchStart);
    window.removeEventListener("touchmove", this.onTouchMove);
    window.removeEventListener("touchend", this.onTouchEnd);
    window.removeEventListener("touchcancel", this.onTouchCancel);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  },
  methods: {
    onTouchStart(e) {
      this.tracking = null;
      // Новое касание — прошлый жест точно кончился. iOS теряет touchend /
      // touchcancel (системный жест от верхнего края, второй палец, уход в
      // фон), и без сброса кружок навсегда застывал на экране.
      this.resetPull();
      if (this.refreshing || !e.touches || e.touches.length !== 1) {
        this.tracking = false;
        return;
      }
      if (!canStartPull(e.target)) {
        this.tracking = false;
        return;
      }
      this.startX = e.touches[0].clientX;
      this.startY = e.touches[0].clientY;
    },
    onTouchMove(e) {
      if (this.tracking === false || !e.touches || !e.touches.length) return;
      const dx = e.touches[0].clientX - this.startX;
      const dy = e.touches[0].clientY - this.startY;
      if (this.tracking === null) {
        // Решаем по первому заметному сдвигу: вниз и вертикально — наш жест,
        // иначе касание отдаём странице (прокрутка, свайп по таблице).
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        if (dy <= 0 || Math.abs(dx) > Math.abs(dy)) {
          this.tracking = false;
          return;
        }
        this.tracking = true;
      }
      if (e.cancelable) e.preventDefault();
      this.dragging = true;
      this.distance = pullDistance(dy);
    },
    onTouchEnd() {
      if (this.tracking !== true) {
        this.tracking = null;
        this.resetPull();
        return;
      }
      const shouldRefresh = this.distance >= PULL_THRESHOLD;
      this.tracking = null;
      this.dragging = false;
      if (shouldRefresh) {
        this.run();
      } else {
        this.distance = 0;
      }
    },
    onTouchCancel() {
      this.tracking = null;
      this.resetPull();
    },
    // Приложение ушло в фон посреди жеста — touchend уже не придёт.
    onVisibilityChange() {
      if (document.hidden) this.onTouchCancel();
    },
    // Убрать недотянутый жест. Идущее обновление не трогаем — его кружок
    // гасит run().
    resetPull() {
      this.dragging = false;
      if (!this.refreshing) this.distance = 0;
    },
    run() {
      this.refreshing = true;
      const started = Date.now();
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        const wait = Math.max(0, MIN_SPIN_MS - (Date.now() - started));
        setTimeout(() => {
          this.refreshing = false;
          this.distance = 0;
        }, wait);
      };
      setTimeout(finish, MAX_SPIN_MS);
      return Promise.resolve()
        .then(() => this.refresh())
        .catch(() => {})
        .then(finish);
    },
  },
};
</script>

<style scoped>
.pull-to-refresh {
  position: fixed;
  left: 50%;
  /* Верх кружка прячется под шапкой: выезжает из-под неё на distance. */
  top: calc(var(--safe-area-top, 0px) + var(--app-bar-height, 56px) - 40px);
  z-index: 4;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.24);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
.pull-to-refresh--settling {
  transition: transform 0.2s ease-out;
}
</style>
